/**
 * Custom Data Provider for Hono API
 */

import type { DataProvider } from '@refinedev/core';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787';

export const dataProvider: DataProvider = {
  getList: async ({ resource, pagination, filters }) => {
    const { current = 1, pageSize = 10 } = pagination ?? {};
    const params = new URLSearchParams({
      page: String(current),
      limit: String(pageSize),
      includeInactive: 'true',
    });

    // Add filters
    filters?.forEach((filter) => {
      if ('field' in filter && filter.value) {
        params.append(filter.field, String(filter.value));
      }
    });

    const response = await fetch(`${API_URL}/admin/${resource}?${params}`, {
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch ${resource}`);
    }

    const { data, meta } = await response.json();

    return {
      data,
      total: meta?.total ?? data.length,
    };
  },

  getOne: async ({ resource, id }) => {
    // Handle singleton resources (no ID in URL)
    const isSingleton = resource === 'site-settings';
    const url = isSingleton
      ? `${API_URL}/admin/${resource}`
      : `${API_URL}/admin/${resource}/${id}`;

    const response = await fetch(url, {
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch ${resource}${isSingleton ? '' : `/${id}`}`);
    }

    const { data } = await response.json();
    return { data };
  },

  create: async ({ resource, variables }) => {
    const response = await fetch(`${API_URL}/admin/${resource}`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(variables),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error?.message || `Failed to create ${resource}`);
    }

    const { data } = await response.json();
    return { data };
  },

  update: async ({ resource, id, variables }) => {
    // Handle singleton resources (no ID in URL)
    const isSingleton = resource === 'site-settings';
    const url = isSingleton
      ? `${API_URL}/admin/${resource}`
      : `${API_URL}/admin/${resource}/${id}`;

    const response = await fetch(url, {
      method: 'PUT',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(variables),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error?.message || `Failed to update ${resource}`);
    }

    const { data } = await response.json();
    return { data };
  },

  deleteOne: async ({ resource, id }) => {
    const response = await fetch(`${API_URL}/admin/${resource}/${id}`, {
      method: 'DELETE',
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error(`Failed to delete ${resource}/${id}`);
    }

    return { data: { id } as any };
  },

  getApiUrl: () => API_URL,

  // Optional methods
  getMany: async ({ resource, ids }) => {
    const promises = ids.map((id) =>
      fetch(`${API_URL}/admin/${resource}/${id}`, { credentials: 'include' }).then((r) =>
        r.json()
      )
    );
    const results = await Promise.all(promises);
    return { data: results.map((r) => r.data) };
  },

  custom: async ({ url, method, payload, headers }) => {
    const response = await fetch(`${API_URL}${url}`, {
      method: method || 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...headers,
      },
      body: payload ? JSON.stringify(payload) : undefined,
    });

    const data = await response.json();
    return { data };
  },
};
